// Glyphs for the site chips: small SVG pictures on a 24 × 24 grid. Filled shapes use `currentColor`,
// cut-outs use `var(--bg)` (the chip's own background), so one string serves any chip colour.
//
//   wonders    temple (cultural) · mountain (natural) · mountain behind a temple (mixed)
//   resources  oil drop · gas flame · coal lumps · uranium trefoil · lithium cell · cobalt gem
//              rare-earth crystals · copper spool · iron gear · gold ingots · nickel coin · graphite sheets

import { RESOURCE, type Wonder } from '../../core/schema.ts';

const GRID = 24;
const HALF = GRID / 2;
const CUT = 'var(--bg)';

function cut(width: number): string {
  return `fill="none" stroke="${CUT}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"`;
}

function pt(x: number, y: number): string {
  return `${x.toFixed(2)} ${y.toFixed(2)}`;
}

/** A blade of the trefoil: the ring sector between two radii and two angles (radians). */
function sector(r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number): string => pt(HALF + Math.cos(a) * r, HALF + Math.sin(a) * r);
  return `M${p(r0, a0)}L${p(r1, a0)}A${r1} ${r1} 0 0 1 ${p(r1, a1)}L${p(r0, a1)}A${r0} ${r0} 0 0 0 ${p(r0, a0)}z`;
}

const TREFOIL_BLADES = 3;
const TREFOIL_HALF_SPAN = Math.PI / 6;
const TREFOIL: string = (() => {
  let d = '';
  for (let k = 0; k < TREFOIL_BLADES; k++) {
    const mid = -Math.PI / 2 + (k * Math.PI * 2) / TREFOIL_BLADES;
    d += sector(3.6, 10.2, mid - TREFOIL_HALF_SPAN, mid + TREFOIL_HALF_SPAN);
  }
  return `<path d="${d}"/><circle cx="${HALF}" cy="${HALF}" r="2"/>`;
})();

const GEAR_TEETH = 8;
const GEAR_OUTER = 10.4;
const GEAR_ROOT = 7.6;
const GEAR_TOOTH = 0.2; // half width of a tooth, in turns of one pitch
const GEAR: string = (() => {
  const pts: string[] = [];
  for (let k = 0; k < GEAR_TEETH; k++) {
    const a = (k / GEAR_TEETH) * Math.PI * 2;
    const step = (Math.PI * 2) / GEAR_TEETH;
    const w = GEAR_TOOTH * step;
    const at = (r: number, off: number): string => pt(HALF + Math.cos(a + off) * r, HALF + Math.sin(a + off) * r);
    pts.push(at(GEAR_ROOT, -step * 0.5 + w), at(GEAR_OUTER, -w), at(GEAR_OUTER, w), at(GEAR_ROOT, step * 0.5 - w));
  }
  return `<path d="M${pts.join('L')}z"/><circle cx="${HALF}" cy="${HALF}" r="3.4" fill="${CUT}"/>`;
})();

const OIL = `<path d="M12 2.2C12 2.2 5.2 9.8 5.2 14.6a6.8 6.8 0 0 0 13.6 0C18.8 9.8 12 2.2 12 2.2z"/>
<path d="M8.6 14.8a3.4 3.4 0 0 0 3.2 3.4" ${cut(1.5)} opacity=".75"/>`;

const GAS = `<path d="M12.6 2c.6 3.4 5.9 6 5.9 11.6a6.5 6.5 0 0 1-13 0c0-2.6 1.1-4.3 2.5-5.5.2 1.7.9 2.7 1.8 3.1C9.3 8.1 10.3 4.4 12.6 2z"/>
<path d="M12 13.2c1.6 1.4 2.6 2.5 2.6 3.8a2.6 2.6 0 0 1-5.2 0c0-1.3 1-2.4 2.6-3.8z" fill="${CUT}"/>`;

const COAL = `<g stroke="${CUT}" stroke-width=".9" stroke-linejoin="round">
<path d="M8.4 11.2 9.8 6.2l4.6-1.4 3.2 3.3-1.4 4.6-3.4-1.5z"/>
<path d="M2.6 20.4 3.8 13.4l5.4-2.2 3.6 3.4-1.2 5.8z"/>
<path d="M11.4 20.4l1.4-6.6 4.8-2.2 4 3-1 5.8z"/></g>`;

const LITHIUM = `<rect x="9.4" y="2.6" width="5.2" height="3.4" rx="1"/>
<rect x="5.8" y="5.4" width="12.4" height="16" rx="2.4"/>
<path d="M13 8.4 9 14.4h2.8l-.8 4.6 4.2-6.4h-2.8z" fill="${CUT}"/>`;

const COBALT = `<path d="M7 3.4h10l5 6-10 11.8L2 9.4z"/>
<path d="M2 9.4h20M7 3.4l2.6 6L12 21.2M17 3.4l-2.6 6L12 21.2" ${cut(0.9)}/>`;

const RARE_EARTHS = `<g stroke="${CUT}" stroke-width=".9" stroke-linejoin="round">
<path d="M12 2.4l3.4 4.4v11.6L12 21.6l-3.4-3.2V6.8z"/>
<path d="M6.4 9l2.6 2.6v7.6L5.6 21.4 2.6 19v-7.4z"/>
<path d="M17.6 9 21.4 11.6V19l-3 2.4-3.4-2.2v-7.6z"/></g>`;

const COPPER = `<rect x="3.6" y="2.6" width="16.8" height="3.6" rx="1.2"/>
<rect x="3.6" y="17.8" width="16.8" height="3.6" rx="1.2"/>
<rect x="6.6" y="6" width="10.8" height="12" rx="1"/>
<path d="M6.6 9.6h10.8M6.6 12h10.8M6.6 14.4h10.8" ${cut(0.9)}/>`;

const GOLD = `<g stroke="${CUT}" stroke-width=".9" stroke-linejoin="round">
<path d="M6.4 12.6l2.2-5.4h6.8l2.2 5.4z"/>
<path d="M1.8 20 4.2 13.4h7.2L13.6 20z"/>
<path d="M10.4 20l2.4-6.6h7.2L22.2 20z"/></g>`;

const NICKEL = `<circle cx="12" cy="12" r="10"/>
<circle cx="12" cy="12" r="7.6" ${cut(0.9)}/>
<text x="12" y="15.4" text-anchor="middle" font-size="9.4" font-weight="700" fill="${CUT}" font-family="Alegreya Sans SC, Trebuchet MS, sans-serif">Ni</text>`;

const GRAPHITE = `<g stroke="${CUT}" stroke-width=".9" stroke-linejoin="round">
<path d="M2.6 16.4 12 12l9.4 4.4L12 21z"/>
<path d="M2.6 11.6 12 7.2l9.4 4.4L12 16.2z"/>
<path d="M2.6 6.8 12 2.4l9.4 4.4L12 11.4z"/></g>`;

const TEMPLE = `<path d="M12 3 2.2 9.2h19.6z"/>
<rect x="3.4" y="10.2" width="17.2" height="1.7"/>
<rect x="4.4" y="13" width="2.5" height="6.2"/>
<rect x="8.8" y="13" width="2.5" height="6.2"/>
<rect x="12.7" y="13" width="2.5" height="6.2"/>
<rect x="17.1" y="13" width="2.5" height="6.2"/>
<rect x="2.4" y="19.8" width="19.2" height="2"/>`;

const MOUNTAIN = `<path d="M1.6 20.6 9.4 5.6l4.2 7.2 2.6-3.6 6.2 11.4z"/>
<path d="M9.4 5.6l2.5 4.6-1.6-.9-.9 1.5-.9-1.5-1.6.9z" fill="${CUT}"/>`;

const MIXED = `<path d="M1.4 20.6 8.6 6.4l4.4 7.4 2.8-4L22.6 20.6z" opacity=".55"/>
<path d="M12 8.6 4 13.4h16z"/>
<rect x="5" y="14.2" width="2.2" height="5.4"/>
<rect x="8.9" y="14.2" width="2.2" height="5.4"/>
<rect x="12.9" y="14.2" width="2.2" height="5.4"/>
<rect x="16.8" y="14.2" width="2.2" height="5.4"/>
<rect x="3.2" y="20" width="17.6" height="1.9"/>`;

const RESOURCE_GLYPH: Readonly<Record<number, string>> = {
  [RESOURCE.OIL]: OIL,
  [RESOURCE.GAS]: GAS,
  [RESOURCE.COAL]: COAL,
  [RESOURCE.URANIUM]: TREFOIL,
  [RESOURCE.LITHIUM]: LITHIUM,
  [RESOURCE.COBALT]: COBALT,
  [RESOURCE.RARE_EARTHS]: RARE_EARTHS,
  [RESOURCE.COPPER]: COPPER,
  [RESOURCE.IRON]: GEAR,
  [RESOURCE.GOLD]: GOLD,
  [RESOURCE.NICKEL]: NICKEL,
  [RESOURCE.GRAPHITE]: GRAPHITE,
};

const WONDER_GLYPH: Readonly<Record<Wonder['category'], string>> = { cultural: TEMPLE, natural: MOUNTAIN, mixed: MIXED };

function svg(body: string): string {
  return `<svg viewBox="0 0 ${GRID} ${GRID}" width="100%" height="100%" fill="currentColor" aria-hidden="true">${body}</svg>`;
}

/** SVG markup of a resource kind's glyph (a RESOURCE value 1..12). */
export function resourceGlyph(kind: number): string {
  return svg(RESOURCE_GLYPH[kind] ?? '');
}

/** SVG markup of a wonder category's glyph. */
export function wonderGlyph(category: Wonder['category']): string {
  return svg(WONDER_GLYPH[category]);
}
